import React from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { OUTAGE_OPTIONS, TIME_RANGE_OPTIONS } from './constants';

const MAX_TAGS = 1;

const outageOptionsStyles = {
  '& .MuiAutocomplete-tag': { borderColor: 'secondary.main', maxWidth: '124px' },
  '& .MuiOutlinedInput-root': {
    flexWrap: 'nowrap',
    maxHeight: '48px',
  },

  width: '250px',
};

const inputStyles = {
  '> .MuiOutlinedInput-input': {
    minWidth: '0 !important',
  },
  minWidth: 0,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
};

const limitTagStyles = {
  backgroundColor: 'secondary.main',
  border: 'none',
  color: 'surface.white',
};

const Filters = ({ filtersState, resetFilters, setFiltersState }) => {
  return (
    <Stack alignItems="center" direction="row" spacing={2}>
      <Autocomplete
        disableClearable
        disableCloseOnSelect
        getOptionLabel={option => option}
        limitTags={MAX_TAGS}
        multiple
        onChange={(_, value) => setFiltersState(R.assoc('outageCategory', value))}
        options={OUTAGE_OPTIONS}
        renderInput={params => (
          <TextField
            {...params}
            label="Outage Category"
            slotProps={{ input: { ...params.InputProps, sx: inputStyles } }}
          />
        )}
        renderOption={(props, option, { selected }) => (
          // eslint-disable-next-line react/jsx-props-no-spreading
          <Box component="li" {...props} sx={{ p: '0 !important' }}>
            <Checkbox
              checked={selected}
              checkedIcon={<CheckBoxIcon />}
              icon={<CheckBoxOutlineBlankIcon />}
            />
            {option}
          </Box>
        )}
        renderTags={(tags, getTagProps) => (
          <>
            {tags.slice(0, MAX_TAGS).map((tag, index) => (
              <Chip
                {...getTagProps({ index })}
                label={
                  <Typography sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }} variant="body2">
                    {tag}
                  </Typography>
                }
                variant="outlined"
              />
            ))}
            {tags.length > MAX_TAGS && (
              <Chip label={`+${tags.length - MAX_TAGS}`} sx={limitTagStyles} variant="outlined" />
            )}
          </>
        )}
        slotProps={{ popper: { placement: 'bottom-start' } }}
        sx={outageOptionsStyles}
        value={filtersState.outageCategory}
      />
      <Autocomplete
        disableClearable
        getOptionLabel={option => option.label}
        isOptionEqualToValue={R.eqProps('value')}
        onChange={(_, value) => setFiltersState(R.assoc('timeRange', value))}
        options={TIME_RANGE_OPTIONS}
        renderInput={params => <TextField {...params} label="Time Range" />}
        sx={{ width: '215px' }}
        value={filtersState.timeRange}
      />
      <Button onClick={resetFilters} sx={{ pl: 0 }} variant="text">
        Reset Filters
      </Button>
    </Stack>
  );
};

Filters.propTypes = {
  filtersState: PropTypes.shape({
    outageCategory: PropTypes.arrayOf(PropTypes.string),
    timeRange: PropTypes.shape({ label: PropTypes.string, value: PropTypes.string }),
  }).isRequired,
  resetFilters: PropTypes.func.isRequired,
  setFiltersState: PropTypes.func.isRequired,
};

export default Filters;
